import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import {
  MinusIcon,
  PlusIcon,
  ChevronUpDownIcon,
  MapPinIcon,
  TruckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

import Modal from '../../shared/components/UI/Modal';
import DatePicker from '../../shared/components/DatePicker';
import * as API from '../../api';
import { cn } from '../../shared/utils/cn';

const TIME_SLOTS = [
  '08h00 : 10h00',
  '10h00 : 12h00',
  '12h00 : 14h00',
  '14h00 : 16h00',
  '16h00 : 18h00',
  '18h00 : 20h00',
  '20h00 : 22h00',
];

const EditModal = ({ shown, close, order }) => {
  const { t } = useTranslation();
  const [event, setEvent] = React.useState({});
  const [placesAvailable, setPlacesAvailable] = React.useState(0);
  const [prices, setPrices] = React.useState([]);
  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const [selectedTime, setSelectedTime] = React.useState('');
  const [cityStart, setCityStart] = React.useState(null);
  const [roader, setRoader] = React.useState(null);
  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [loadingUpdating, setLoadingUpdating] = React.useState(false);
  const [settings, setSettings] = React.useState({});
  const [roaders, setRoaders] = React.useState([]);
  const [orderHelper, setOrderHelper] = React.useState({ type: '', message: '' });

  // ---- logic (preserved) ---------------------------------------------------

  const totalPrice = (_prices) => {
    let total = 0;
    _prices.forEach((price) => {
      total = total + (price.total_price || 0);
    });
    return total;
  };

  const handleChangeCheckbox = (item) => {
    let exept = prices.filter((p) => p.id != item.id);
    item.status = !item.status;
    item.qt = item.status ? 1 : 0;
    item.total_price = item.price * item.qt;
    setPrices([item, ...exept]);
  };

  const handleChangeQt = (item, nQt) => {
    let exept = prices.filter((p) => p.id != item.id);
    item.qt = nQt < 1 ? '' : nQt;
    item.total_price = item.price * item.qt;
    setPrices([item, ...exept]);
  };

  const submitBooking = async () => {
    setOrderHelper({ type: '', message: '' });
    setLoadingUpdating(true);

    let form = {
      reservation_id: order.id,
      prices: prices.filter((p) => p.qt > 0),
    };
    let _form = form;
    if (event.type == 'Activitie') {
      _form = Object.assign(form, { date_activity: selectedDate, time_activity: selectedTime });
    }
    if (event.type == 'Event') {
      _form = Object.assign(form, { depart_city: cityStart, roader: roader });
    }

    API.updateOrder(_form)
      .then(() => {
        setOrderHelper({ type: 'success', message: t('account.editSuccess') });
      })
      .catch(() => {})
      .then(() => {
        setLoadingUpdating(false);
        window.location.reload();
      });
  };

  React.useEffect(() => {
    if (!Object.keys(order).length) return;
    const ev = order.event;
    setEvent(ev);
    setSelectedDate(order.date_depart_activity);
    setSelectedTime(order.time_activity);

    let pricesList = [];
    ev.prices.forEach((ep) => {
      let old = order.prices.find((p) => p.name === ep.name);
      if (old) pricesList.push({ ...old, id: ep.id });
      else pricesList.push({ ...ep, qt: 0 });
    });
    setPrices(pricesList);

    setPlacesAvailable(ev.places_available);
    if (!parseInt(order.etat)) {
      let dflt = ev.places_available;
      pricesList.forEach((pr) => {
        dflt -= parseInt(pr.qt);
      });
      setPlacesAvailable(dflt);
    }

    setCityStart(order.depart_city);

    setLoadingSettings(true);
    API.Settings()
      .then((res) => {
        setSettings(res.settings);
        setRoaders(res.roaders);
      })
      .then(() => setLoadingSettings(false));
    setRoader(order.roader);
  }, [order]);

  React.useEffect(() => {
    let total_places = 0;
    prices.forEach((price) => {
      if (!parseInt(order.etat)) total_places = parseInt(total_places) + parseInt(price.qt || 0);
    });
    setPlacesAvailable((event.places_available || 0) - total_places);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  const disabled =
    (placesAvailable < 0 && event.type == 'Event') ||
    (event.type == 'Event' && !cityStart) ||
    (event.type !== 'Event' && parseInt(event.hour_status) && !selectedTime) ||
    !totalPrice(prices) ||
    (event.covoiturage == '1' && !roader) ||
    loadingUpdating;

  if (!Object.keys(event).length) return <Modal shown={shown} close={close} size="modal--large" />;

  const sorted = [...prices].sort((a, b) => (a.name > b.name ? 1 : -1));

  return (
    <Modal shown={shown} close={close} size="modal--large" title={t('account.editTitle')} subtitle={event.title}>
      {orderHelper.message ? (
        <div
          className={cn(
            'mb-4 flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-sm',
            orderHelper.type === 'success'
              ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
              : 'border-rose-100 bg-rose-50 text-rose-600'
          )}
        >
          {orderHelper.type === 'success' ? (
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div dangerouslySetInnerHTML={{ __html: orderHelper.message }} />
        </div>
      ) : null}

      <div className="space-y-5">
        {/* Date / type block */}
        {event.type === 'Activitie' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('booking.chooseDate')}>
              <DatePicker value={selectedDate} minDate={new Date()} onChange={(d) => setSelectedDate(d)} placeholder={t('booking.chooseDate')} />
            </Field>
            {parseInt(event.hour_status) ? (
              <Field label={t('booking.chooseTime')}>
                <SelectBox
                  value={selectedTime}
                  onChange={setSelectedTime}
                  placeholder={t('booking.chooseTime')}
                  options={TIME_SLOTS.map((s) => ({ value: s, label: s }))}
                />
              </Field>
            ) : null}
          </div>
        ) : null}

        {event.type === 'Event' ? (
          <div className="grid grid-cols-3 gap-3">
            <Stat label={t('booking.dateDepart')} value={new Date(event.date_depart).toLocaleDateString('fr-FR')} />
            <Stat label={t('booking.dateEnd')} value={new Date(event.date_end).toLocaleDateString('fr-FR')} />
            <Stat label={t('booking.placesAvailable')} value={placesAvailable} danger={placesAvailable <= 0} />
          </div>
        ) : null}

        {/* Tickets */}
        <div className="rounded-xl border border-slate-100 bg-white">
          <div className="divide-y divide-slate-100 px-4">
            {sorted.map((item) => {
              item.total_price = parseFloat(item.price) * parseInt(item.qt || 0);
              const qt = parseInt(item.qt) || 0;
              const active = qt > 0;
              return (
                <div key={item.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3.5">
                  <button
                    type="button"
                    onClick={() => handleChangeCheckbox(item)}
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                      active ? 'border-brand-500 bg-brand-500 text-white' : 'border-slate-300 bg-white'
                    )}
                  >
                    {active ? <CheckIcon className="h-3.5 w-3.5" /> : null}
                  </button>
                  <div className="min-w-[35%] flex-1">
                    <p className="text-sm font-medium text-ink">{item.name}</p>
                    <p className="text-xs text-ink-subtle">{API.priceFormat(item.price)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <StepBtn icon={MinusIcon} disabled={!active || qt <= 1} onClick={() => handleChangeQt(item, qt - 1)} />
                    <input
                      type="number"
                      value={item.qt}
                      disabled={!active}
                      onChange={(e) => handleChangeQt(item, e.target.value)}
                      className="w-12 appearance-none rounded-lg border border-slate-200 py-1.5 text-center text-sm text-ink [-moz-appearance:textfield] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 disabled:text-ink-subtle [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <StepBtn icon={PlusIcon} disabled={!active} onClick={() => handleChangeQt(item, qt + 1)} />
                  </div>
                  <div className="w-24 shrink-0 text-right rtl:text-left">
                    <bdi className="text-sm font-semibold text-brand-500">{API.priceFormat(item.total_price)}</bdi>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trip: city + carpool */}
        {event.type === 'Event' ? (
          <div className="space-y-4">
            <Field label={t('booking.departureCity')} hint={t('booking.departureCityHint')} icon={MapPinIcon}>
              <SelectBox
                value={cityStart}
                onChange={setCityStart}
                placeholder={t('booking.departureCityPlaceholder')}
                options={(event.gathering || []).map((g) => ({ value: g.gathering, label: g.gathering }))}
              />
            </Field>
            {event.covoiturage == '1' ? (
              <Field label={t('booking.carpool')} hint={t('booking.carpoolHint')} icon={TruckIcon}>
                <SelectBox
                  value={roader}
                  onChange={setRoader}
                  placeholder={t('booking.carpoolPlaceholder')}
                  loading={loadingSettings}
                  options={roaders.map((r) => ({ value: r.id, label: r.name }))}
                />
              </Field>
            ) : null}
          </div>
        ) : null}

        {/* Total + save */}
        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{t('booking.total')}</p>
            <bdi className="font-display text-xl font-bold text-brand-500">{API.priceFormat(totalPrice(prices))}</bdi>
          </div>
          <button
            type="button"
            onClick={submitBooking}
            disabled={disabled}
            className="flex min-w-[180px] items-center justify-center rounded-xl bg-accent-500 px-5 py-3 text-sm font-bold text-brand-900 shadow-card transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingUpdating ? (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-brand-900/30 border-t-brand-900" />
            ) : (
              t('account.saveChanges')
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ---- helpers ----------------------------------------------------------------

function Field({ label, hint, icon: Icon, children }) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-ink">
        {Icon ? <Icon className="h-4 w-4 text-brand-500" /> : null}
        {label}
      </label>
      {hint ? <p className="mb-2 text-xs text-ink-subtle">{hint}</p> : null}
      {children}
    </div>
  );
}

function Stat({ label, value, danger }) {
  return (
    <div className="rounded-xl bg-surface-muted p-3 text-center">
      <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</p>
      <p className={cn('mt-1 font-display text-sm font-bold', danger ? 'text-rose-500' : 'text-ink')}>{value}</p>
    </div>
  );
}

function StepBtn({ icon: Icon, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand-500 hover:text-brand-500 disabled:opacity-40"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function SelectBox({ value, onChange, options, placeholder, loading }) {
  const current = options.find((o) => String(o.value) === String(value));
  return (
    <Listbox value={value ?? ''} onChange={onChange} disabled={loading}>
      <div className="relative">
        <Listbox.Button
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-start text-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:opacity-60',
            current ? 'text-ink' : 'text-ink-subtle'
          )}
        >
          <span className="truncate">{loading ? '…' : current ? current.label : placeholder}</span>
          <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-ink-subtle" />
        </Listbox.Button>
        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <Listbox.Options className="absolute z-50 mt-2 max-h-56 w-full overflow-auto rounded-xl border border-slate-100 bg-white py-1.5 text-sm shadow-elevated focus:outline-none">
            {options.map((o) => (
              <Listbox.Option
                key={o.value}
                value={o.value}
                className={({ active }) =>
                  cn('relative cursor-pointer select-none py-2.5 pl-9 pr-3 rtl:pl-3 rtl:pr-9', active ? 'bg-brand-50 text-brand-700' : 'text-ink')
                }
              >
                {({ selected }) => (
                  <>
                    <span className={cn('block truncate', selected && 'font-semibold')}>{o.label}</span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-500 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3">
                        <CheckIcon className="h-4 w-4" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

export default EditModal;
